import React from 'react';
import AeroIcon from './AeroIcon';
import './AeroStatePanel.css';

/**
 * AeroStatePanel: the one v3 panel for a page or list that has nothing to
 * show (phase 7b): not found, no access, offline, a generic error and empty.
 * A quiet round mark, a title, one plain line and an optional way out. Flat,
 * tokens only (AeroStatePanel.css). Class names are its own, so it works
 * inside or outside `.ut-theme`.
 *
 * Never pass a raw Firebase or JS message as the line: log it with
 * console.error and let `stateFromError` pick the kind.
 *
 * Props:
 *   kind     'notfound' | 'noaccess' | 'offline' | 'error' | 'empty'
 *            (sets the default icon, title and line)
 *   title    overrides the default title ("Site not found")
 *   children overrides the default line
 *   icon     overrides the default AeroIcon name; null for no mark
 *   actions  buttons (GlassButton: ink for the one way out, glass for the second)
 *   compact  less padding, for a panel inside a card or a list
 */
export const AERO_STATE_DEFAULTS = {
  notfound: {
    icon: 'search',
    title: 'Not found',
    line: 'It may have been deleted, or the link may be wrong.',
  },
  noaccess: {
    icon: 'lock',
    title: "You don't have access",
    line: 'Ask its owner to share it with you, or sign in with the account that made it.',
  },
  offline: {
    icon: 'offline',
    title: "You're offline",
    line: "Aero can't be reached right now. Check your connection, then try again.",
  },
  error: {
    icon: 'alert',
    title: 'Something went wrong',
    line: 'It may be a short hiccup on our side. Try again in a moment.',
  },
  empty: {
    icon: 'info',
    title: 'Nothing here yet',
    line: null,
  },
};

export default function AeroStatePanel({
  kind = 'error',
  title,
  children,
  icon,
  actions = null,
  compact = false,
  className = '',
}) {
  const d = AERO_STATE_DEFAULTS[kind] || AERO_STATE_DEFAULTS.error;
  const mark = icon === undefined ? d.icon : icon;
  const line = children === undefined ? d.line : children;
  const live = kind === 'offline' || kind === 'error';
  return (
    <div
      className={`aero-state aero-state-${kind}${compact ? ' aero-state-compact' : ''} ${className}`.trim()}
      role={live ? 'alert' : 'status'}
    >
      {mark && (
        <span className="aero-state-mark" aria-hidden="true">
          <AeroIcon name={mark} />
        </span>
      )}
      <h2 className="aero-state-title">{title || d.title}</h2>
      {line && <p className="aero-state-line">{line}</p>}
      {actions && <div className="aero-state-actions">{actions}</div>}
    </div>
  );
}

/**
 * Turn a load error into a state kind, so the page shows plain words and the
 * raw message only reaches the console. Firestore reports "client is
 * offline" as code 'unavailable', and a rules refusal as 'permission-denied'.
 */
export function stateFromError(err) {
  if (!err) return 'notfound';
  const code = String(err.code || '');
  const msg = String(err.message || err);
  if (
    code === 'unavailable' ||
    code === 'deadline-exceeded' ||
    /offline|network|failed to fetch|unavailable/i.test(msg) ||
    (typeof navigator !== 'undefined' && navigator.onLine === false)
  ) {
    return 'offline';
  }
  if (code === 'permission-denied' || code === 'unauthenticated' || /permission/i.test(msg)) {
    return 'noaccess';
  }
  return 'error';
}

/**
 * The plain reason for a toast after an action fails ("Could not rename the
 * site. " + errorLine(err)). Same kinds as stateFromError; the raw message
 * belongs in console.error, never in the toast.
 */
export function errorLine(err) {
  const kind = stateFromError(err || { message: '' });
  if (kind === 'offline') return "You're offline. Check your connection, then try again.";
  if (kind === 'noaccess') return "You don't have access to do that.";
  return 'Try again in a moment.';
}
